import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { navigation } from "../../data/home";
import { getLanguageSwitchPath, isPersianPath, normalizePath, persianNavigation } from "../../i18n/routes";
import { Brand } from "../ui/Brand";
import { Icon } from "../ui/Icon";

export function Header() {
  const location = useLocation();
  const isFa = isPersianPath(location.pathname);
  const navigationItems = isFa ? persianNavigation : navigation;
  const languageHref = getLanguageSwitchPath(location.pathname);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const isActive = (href: string) => {
    const [path, hash] = href.split("#");
    if (hash === "insights") return location.pathname === path && location.hash === `#${hash}`;
    if (path === "/" || path === "/fa/") return normalizePath(location.pathname) === path && !location.hash;
    return normalizePath(location.pathname) === path || location.pathname.startsWith(`${path}/`);
  };
  const closeMenu = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        buttonRef.current?.focus();
      }
      if (event.key === "Tab") {
        const controls = panelRef.current?.querySelectorAll<HTMLElement>('a, button');
        const first = controls?.[0];
        const last = controls?.[controls.length - 1];
        if (!panelRef.current?.contains(document.activeElement)) {
          event.preventDefault();
          first?.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last?.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first?.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const focusFrame = requestAnimationFrame(() => {
      panelRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
    });
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  return (
    <header className="site-header" id="top">
      <div className="header-inner">
        <Brand homeHref={isFa ? "/fa/#top" : "/#top"} label={isFa ? "صفحه اصلی IranTradeReach" : "IranTradeReach home"} />
        <nav className="desktop-nav" aria-label={isFa ? "ناوبری اصلی" : "Primary navigation"}>
          {navigationItems.map((item) => (
            <Link className={isActive(item.href) ? "active" : ""} aria-current={isActive(item.href) ? "page" : undefined} to={item.href} key={item.label}>{item.label}</Link>
          ))}
        </nav>
        <div className="header-actions">
          <Link className="language-button" to={languageHref} lang={isFa ? "en" : "fa"} hrefLang={isFa ? "en" : "fa"} aria-label={isFa ? "Switch to English" : "نمایش نسخه فارسی"}>{isFa ? "EN" : "FA"}</Link>
          <Link className="button button--small header-quote" to={isFa ? "/fa/suppliers/join" : "/products?rfq=open#rfq"}>{isFa ? "معرفی شرکت" : "Request Sourcing"}</Link>
          <button
            ref={buttonRef}
            className="menu-button"
            type="button"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? (isFa ? "بستن منو" : "Close menu") : (isFa ? "باز کردن منو" : "Open menu")}
            onClick={() => setOpen((value) => !value)}
          >
            <Icon name={open ? "x" : "menu"} size={22} />
          </button>
        </div>
      </div>
      <button className={`menu-backdrop ${open ? "is-open" : ""}`} aria-label={isFa ? "بستن منو" : "Dismiss menu"} tabIndex={-1} onClick={closeMenu} />
      <div ref={panelRef} id="mobile-menu" className={`mobile-menu ${open ? "is-open" : ""}`} role="dialog" aria-modal={open || undefined} aria-label={isFa ? "ناوبری" : "Navigation"} aria-hidden={!open} inert={!open} onTransitionEnd={(event) => {
        if (open && event.target === event.currentTarget && !event.currentTarget.contains(document.activeElement)) {
          event.currentTarget.querySelector<HTMLButtonElement>("button")?.focus();
        }
      }}>
        <div className="mobile-menu-heading"><span>{isFa ? "بخش‌های سایت" : "Explore IranTradeReach"}</span><button type="button" className="menu-close" aria-label={isFa ? "بستن ناوبری" : "Close navigation"} onClick={closeMenu}><Icon name="x" size={24} /></button></div>
        <nav aria-label={isFa ? "ناوبری موبایل" : "Mobile navigation"}>
          {navigationItems.map((item) => (
            <Link to={item.href} aria-current={isActive(item.href) ? "page" : undefined} key={item.label} tabIndex={open ? 0 : -1} onClick={() => setOpen(false)}>{item.label}</Link>
          ))}
          <Link className="button" to={isFa ? "/fa/suppliers/join" : "/products?rfq=open#rfq"} tabIndex={open ? 0 : -1} onClick={() => setOpen(false)}>{isFa ? "معرفی شرکت" : "Request Sourcing"}</Link>
        </nav>
      </div>
    </header>
  );
}
